import { useEffect, useRef, useState } from 'react';
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import "../styles/homeCarousel.scss";
import YourParivarCard from "../components/YourParivar/YourParivarCard";
import Seo from "../components/Seo/Seo";
import CustomButton from "../components/button/Button";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal/Reveal";
import Tilt3D from "../components/Tilt3D/Tilt3D";
import DivineParticles from "../components/DivineParticles/DivineParticles";
import CosmicField from "../components/CosmicField/CosmicField";
import {
  SectionHeading,
  GoldenAura,
  MandalaBg,
} from "../components/Ornaments/Ornaments";
import { homeImages } from '../config/homeImages';
import { newsEventApi } from '../api/newsEvents';
import { mediaUrl } from '../api/client';
import { useParivar } from '../context/ParivarContext';

// Lazy-load heavy below-fold components so the hero paints instantly.
const JourneyTimeline = React.lazy(() => import('../components/JourneyTimeline/JourneyTimeline'));
const GujaratiCalendar = React.lazy(() => import('../components/GujaratiCalendar/GujaratiCalendar'));
const YouTubeLive = React.lazy(() => import('../components/YouTubeLive/YouTubeLive'));

const YOUTUBE_URL = 'https://www.youtube.com/@AvdhootVishramSarkhej';

const HERO_QUOTE =
  '"True happiness lies in selfless service and devotion."';

// Gentle pointer parallax for hero layers (desktop + reduced-motion aware).
function useParallax(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = (e.clientX - rect.left) / rect.width - 0.5;
      const cy = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty('--px', cx.toFixed(3));
      el.style.setProperty('--py', cy.toFixed(3));
      el.style.setProperty('--rx', cy.toFixed(3));
      el.style.setProperty('--ry', cx.toFixed(3));
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [ref]);
}

// ============================================================================
// Hero — cinematic, frameless Bapji (2) with AI ambient light field
// ============================================================================
const HeroSection = React.memo(function HeroSection() {
  const navigate = useNavigate();
  const stageRef = useRef(null);
  useParallax(stageRef);

  return (
    <section className="home-hero" ref={stageRef}>
      <MandalaBg className="hero-mandala" />
      <CosmicField density={70} speed={1.1} className="home-hero-cosmic" />
      <DivineParticles density={30} className="home-hero-particles" />
      <div className="home-hero-overlay"></div>

      <div className="home-hero-grid">
        <div className="home-hero-text">
          <Reveal direction="up" initial>
            <p className="hero-chant">॥ श्री गुरुदेव दत्त ॥</p>
            <h1 className="home-hero-title">
              Shri <span className="gradient-text gold-shimmer">Rang Avdhoot</span> Gurumaharaj
            </h1>
            <p className="hero-sarkhej">
              <span className="hero-guj-mantra">॥ परस्पर देवो भव: ॥</span>
            </p>
            <h2 className="hero-place-title">Shri Avdhoot Vishram, Sarkhej</h2>
            <p className="hero-place-desc">
              Avdhoot Vishram, Sarkhej, Ahmedabad, is a sacred spiritual centre
              dedicated to Lord Dattatreya and Shri Rang Avdhoot Gurumaharaj.
              Discover the temple&apos;s history, sacred Atmaling and Padukas,
              Dattopasana, Guru Bhakti, bhajans, publications, festivals and
              spiritual traditions.
            </p>
          </Reveal>
          <Reveal direction="up" delay={240}>
            <div className="home-hero-actions">
              <CustomButton
                variant="outline-dark"
                className="hero-btn-solid av-sheen"
                onClick={() => navigate('/news-events')}
              >
                News &amp; Events
              </CustomButton>
              <CustomButton
                variant="outline-dark"
                className="hero-btn-ghost"
                onClick={() => window.open(YOUTUBE_URL, '_blank', 'noopener,noreferrer')}
              >
                <svg viewBox="0 0 576 512" width="16" height="14" aria-hidden="true" focusable="false" style={{ fill: 'currentColor' }}><path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"/></svg>{' '}
                Watch YouTube
              </CustomButton>
            </div>
          </Reveal>
          <Reveal direction="up" delay={360}>
            <div className="home-hero-quote">
              <i className="fas fa-quote-left"></i>
              <span>{HERO_QUOTE}</span>
            </div>
          </Reveal>
        </div>

        <div className="home-hero-portrait">
          <Reveal direction="zoom" delay={200}>
            <div className="portrait-stage">
              <GoldenAura className="portrait-aura" />
              <div className="portrait-light" aria-hidden="true"></div>
              <div className="portrait-om" aria-hidden="true">ॐ</div>
              <img
                src={homeImages.bapjiFront}
                srcSet={`${homeImages.bapjiFrontSmall} 480w, ${homeImages.bapjiFront} 900w`}
                sizes="(max-width: 768px) 85vw, 45vw"
                alt="Pujya Rang Avdhoot Maharaj"
                className="hero-portrait-img is-transparent"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </Reveal>
        </div>
      </div>

      <div className="home-hero-scroll">
        <span></span>
      </div>
    </section>
  );
});

// ============================================================================
// Divine Presence — frameless Bapji (1), emerging from light
// ============================================================================
const DivinePresenceSection = React.memo(function DivinePresenceSection() {
  const navigate = useNavigate();
  return (
    <section className="divine-presence av-section">
      <div className="section-container">
        <div className="divine-presence-grid">
          <Reveal direction="left" className="divine-img-col">
            <div className="divine-frame-wrap">
              <GoldenAura className="divine-frame-aura" />
              <div className="divine-light" aria-hidden="true"></div>
              <img
                src={homeImages.bapji}
                alt="Shri Rang Avdhoot Maharaj"
                className="divine-img"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div className="divine-text">
            <Reveal direction="up">
              <SectionHeading
                chip="Who We Are"
                title="The Divine Presence of Nareshwar"
                align="left"
                noDivider
              />
            </Reveal>
            <Reveal direction="up" delay={150}>
              <p>
                Born <b>Pandurang Vitthalapant Valame</b> on 21 November 1898 at Godhra,
                Gujarat — he was one of the first thirteen students of Mahatma Gandhi's
                Gujarat Vidyapith, served as a teacher and independence activist, and
                then renounced everything to live in tune with Infinity on the banks of
                the holy Narmada at <b>Nareshwar</b> in 1925.
              </p>
            </Reveal>
            <Reveal direction="up" delay={300}>
              <p>
                Through <b>Dava</b> (Ayurvedic medicine &amp; surgical camps) and{" "}
                <b>Duva</b> (spiritual benediction) he served all who came to him,
                and authored <b>45+ sacred works</b> — including the beloved{" "}
                <i>Datta Bavani</i> — in Gujarati, Sanskrit, Hindi and English.
              </p>
            </Reveal>
            <Reveal direction="up" delay={420}>
              <div className="welcome-actions">
                <CustomButton variant="outline-dark" className="hero-btn-solid" onClick={() => navigate('/about')}>
                  Read Our Story
                </CustomButton>
                <CustomButton variant="outline-dark" className="hero-btn-ghost" onClick={() => navigate('/gallery-category')}>
                  View Gallery
                </CustomButton>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
});

// ============================================================================
// Journey of Grace — flowing spiritual timeline
// ============================================================================
const JOURNEY = [
  {
    era: 'તા. ૨૧-૧૧-૧૮૯૮',
    icon: 'fas fa-baby',
    title: 'પ્રાદુર્ભાવ',
    text: 'સોમવાર, વિઠ્ઠલ મંદિર ગોધરામાં પ્રદોષ સમયે પ્રાદુર્ભાવ.',
  },
  {
    era: '૧૯૦૧',
    icon: 'fas fa-hand-holding-heart',
    title: 'પિતાશ્રીનો આદેશ',
    text: 'પિતાશ્રી તરફથી રામ રામ કરવા આદેશ.',
  },
  {
    era: 'તા. ૧૧-૦૨-૧૯૦૪',
    icon: 'fas fa-book-open',
    title: '"પોથી વાંચ" નો દૈવી આદેશ',
    text: '"પોથી વાંચ" નો દૈવી આદેશ.',
  },
  {
    era: '૧૯૧૪',
    icon: 'fas fa-graduation-cap',
    title: 'ગુજરાત કોલેજમાં',
    text: 'ગુજરાત કોલેજમાં.',
  },
  {
    era: '૧૯૨૦',
    icon: 'fas fa-hands-helping',
    title: 'અસહકાર ચળવળમાં',
    text: 'વડોદરા કોલેજમાં અસહકાર ચળવળમાં ઝંપલાવ્યું.',
  },
  {
    era: '૧૯૨૧',
    icon: 'fas fa-map-pin',
    title: 'સરખેજ સાથે પ્રથમ પરિચય',
    text: 'અમદાવાદ રાષ્ટ્રીય વિનય મંદિર (હાલની મોડલ હાઈસ્કૂલ)માં સરખેજ સાથે પ્રથમ પરિચય. સરખેજમાં અવારનવાર આગમન, અવધૂત તરીકે સૌ પ્રથમ પિછાન.',
  },
  {
    era: 'તા. ૨૪-૦૯-૧૯૨૫',
    icon: 'fas fa-map-signs',
    title: 'રણાપુર આગમન',
    text: 'રણાપુર આગમન, નારેશ્વર સ્થાનની પસંદગી.',
  },
  {
    era: 'તા. ૦૪-૧૨-૧૯૨૫',
    icon: 'fas fa-praying-hands',
    title: 'નારેશ્વરમાં ઉપાસના',
    text: 'નારેશ્વરમાં ઉપાસના અર્થે આગમન, દત્ત પુરાણના ૧૦૮ પારાયણનો આરંભ.',
  },
  {
    era: 'તા. ૦૬-૧૦-૧૯૨૭',
    icon: 'fas fa-hands',
    title: 'શ્રી ગાંડા મહારાજ પાસે',
    text: 'વાકડી શ્રી ગાંડા મહારાજ પાસે, સ્વામી મહારાજના આદેશ મુજબ.',
  },
  {
    era: 'તા. ૦૪-૦૪-૧૯૨૮',
    icon: 'fas fa-water',
    title: 'પ્રથમ પરિક્રમા',
    text: 'પ્રથમ પરિક્રમા, જામફળીમાં પ્રથમ આગમન.',
  },
  {
    era: 'તા. ૦૮-૦૫-૧૯૨૮',
    icon: 'fas fa-book-reader',
    title: 'મંત્ર મહોદધિનું વાંચન',
    text: 'જામફળીમાં મંત્ર મહોદધિનું વાંચન.',
  },
  {
    era: 'જુલાઈ ૧૯૨૯',
    icon: 'fas fa-feather-alt',
    title: 'શ્રી ગુરુચરિત્ર સપ્તશતી સમનુવાદ',
    text: 'જામફળીમાં ફરી મુકામ, શ્રી ગુરુચરિત્ર સપ્તશતી સમનુવાદનું લેખન. સ્વામી મહારાજના દર્શન, ચરિતામૃત ઘટ લે સુખનો પ્રસાદ. શ્રી ગુરુલીલામૃત ગ્રંથ રચનાની શરૂઆત (મદાલસા આખ્યાન સુધી).',
  },
  {
    era: '૧૯૩૦',
    icon: 'fas fa-route',
    title: 'બીજી નર્મદા પરિક્રમા',
    text: 'બીજી નર્મદા પરિક્રમા.',
  },
  {
    era: '૧૯૩૫',
    icon: 'fas fa-tent',
    title: 'સરખેજમાં મુકામ',
    text: 'સરખેજ કોટડાવાળા ખેતરમાં મુકામ, સહજ મહાદેવમાં મુકામ, દત્તબાવનીનો પ્રસાદ, કોટેશ્વર મહાદેવમાં આગમન.',
  },
  {
    era: 'તા. ૧૫-૦૮-૧૯૩૮',
    icon: 'fas fa-hands-praying',
    title: 'પ્રથમ પાદુકા પૂજન ભક્ત મંડળ',
    text: 'અમદાવાદમાં આગમન, પ્રથમ પાદુકા પૂજન ભક્ત મંડળની સ્થાપના, વેજલપુર વૈજનાથ મહાદેવમાં મુકામ, કોટેશ્વર મહાદેવમાં એક માસ મુકામ, પૂજન અર્થે પાદુકાજીની પ્રસાદી.',
  },
  {
    era: 'તા. ૨૯-૧૨-૧૯૩૯',
    icon: 'fas fa-map-marker-alt',
    title: 'સાંથલ મુકામ',
    text: 'સાંથલ મુકામ.',
  },
  {
    era: '૧૯૪૧',
    icon: 'fas fa-home',
    title: 'સરખેજમાં પુનિત પદાર્પણ',
    text: 'સરખેજ શ્રી મહંતના નવા મકાનમાં પુનિત પદાર્પણ.',
  },
  {
    era: '૧૯૪૨',
    icon: 'fas fa-place-of-worship',
    title: 'હાલના સરખેજ સ્થાનમાં',
    text: 'હાલના સરખેજ સ્થાનમાં મુકામ.',
  },
  {
    era: '૧૯૪૪',
    icon: 'fas fa-crown',
    title: 'ગુરુપૂર્ણિમાનો પ્રથમ ઉત્સવ',
    text: 'કેદાર મહાદેવમાં એક માસ મુકામ, પાલનપુર આગમન. ત્યાંથી જુનાવાડજ કાશી વિશ્વનાથ મહાદેવમાં મુકામ, સરખેજમાં આગમન, ગુરુપૂર્ણિમાનો પ્રથમ ઉત્સવ.',
  },
  {
    era: '૧૯૪૫',
    icon: 'fas fa-mountain',
    title: 'આબુ મુકામ',
    text: 'આબુ એક માસ મુકામ.',
  },
  {
    era: 'તા. ૧૯-૦૬-૧૯૪૪',
    icon: 'fas fa-church',
    title: 'ભદ્રેશ્વર મહાદેવમાં મુકામ',
    text: 'સરખેજ પાસે કાસીન્દ્રા ભદ્રેશ્વર મહાદેવમાં એક માસનો મુકામ, બોરીયા મહાદેવમાં દોઢ માસ મુકામ.',
  },
  {
    era: '૧૯૫૨',
    icon: 'fas fa-bus',
    title: 'ખંભાત–ધુવારણ મુકામ',
    text: 'ખંભાત તથા ધુવારણ મુકામ, ડીસા મુકામ.',
  },
  {
    era: 'તા. ૦૧-૧૦-૧૯૫૨',
    icon: 'fas fa-gift',
    title: 'પાદુકાજીનો સરખેજ મંડળને પ્રસાદ',
    text: '"આત્મલિંગ" આપું છું કહી ઉપાસના અર્થે પાદુકાજીનો સરખેજ મંડળને પ્રસાદ, ગુરુદ્વાદશીના દિવસે ગુફામાં પધરામણી.',
  },
  {
    era: '૧૯૫૪',
    icon: 'fas fa-fire',
    title: 'દત્તયાગનું સૌ પ્રથમ આયોજન',
    text: 'ખેડા-સોમનાથ મહાદેવમાં પધરામણી, સરખેજ શ્રી રંગ કુટિરમાં દશ દિવસનો મુકામ, દત્ત જયંતિનો ઉત્સવ, દત્તયાગનું સૌ પ્રથમ આયોજન કમલાકૃતિ ફંડ [?].',
  },
  {
    era: 'તા. ૨૮-૦૫-૧૯૫૯',
    icon: 'fas fa-gopuram',
    title: 'પ્રતિમા સ્થાપના',
    text: 'પૂજ્યશ્રીની પ્રતિમાનું જગદ્ગુરુ શંકરાચાર્યશ્રી અભિનવ સચ્ચિદાનંદજીના હસ્તે સ્થાપના.',
  },
  {
    era: '૧૯૬૨',
    icon: 'fas fa-map-marked-alt',
    title: 'કણભા રંગ જયંતિ સ્થાનમાં',
    text: 'કણભા રંગ જયંતિ સ્થાનમાં આગમન.',
  },
  {
    era: 'તા. ૧૩-૦૪-૧૯૬૪',
    icon: 'fas fa-palette',
    title: 'તેલચિત્રનું અનાવરણ',
    text: 'શ્રી સાગર મહારાજના તેલચિત્રનું ચિત્રાલ મુકામે અનાવરણ.',
  },
  {
    era: '૧૯૬૫',
    icon: 'fas fa-ring',
    title: 'પાદુકા સ્થાપના',
    text: 'રાણપુર, તા. ધંધુકા એક માસ મુકામ. આરસની અભિમંત્રિત કરેલી પાદુકાની ઔદુમ્બર નીચે સ્થાપના.',
  },
  {
    era: 'તા. ૨૧-૧૧-૧૯૬૮',
    icon: 'fas fa-dove',
    title: 'મહાપ્રયાણ — નારેશ્વર સમર્પણ',
    text: 'સરખેજથી શણગારેલી મોટરમાં પૂજ્યશ્રીના પાર્થિવ દેહને અમદાવાદ એરોડ્રામથી નારેશ્વર ભજન ધૂન સાથે લઈ ગયા.',
  },
  {
    era: 'તા. ૦૨-૧૧-૧૯૯૧',
    icon: 'fas fa-mosque',
    title: 'શિખર પ્રતિષ્ઠા',
    text: 'શિખર પ્રતિષ્ઠા, સુવર્ણ કલશ સ્થાપન.',
  },
  {
    era: 'તા. ૦૫-૦૪-૨૦૦૪',
    icon: 'fas fa-om',
    title: '૧૫૦મી જન્મજયંતિ',
    text: 'ચૈત્ર સુદ પૂનમ, હનુમાન જયંતિ, પ.પૂ. શ્રી વાસુદેવાનંદ સરસ્વતી સ્વામી મહારાજની ૧૫૦મી જન્મજયંતિની મૂર્તિનું પૂજ્ય પ્રેમ અવધૂતના વરદ હસ્તે અનાવરણ થયેલ.',
  },
];

const JourneySection = React.memo(function JourneySection() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? JOURNEY : JOURNEY.slice(0, 6);
  return (
    <section className="journey-section av-section">
      <CosmicField density={45} className="journey-cosmic" />
      <div className="section-container">
        <SectionHeading
          chip="The Spiritual Journey"
          title="સરખેજ તો મારું પિયર છે"
          subtitle="શ્રી રંગ અવધૂત"
        />
        <JourneyTimeline items={visible} />
        {!showAll && (
          <div className="journey-load-more-wrap">
            <button className="journey-load-more" onClick={() => setShowAll(true)}>
              <i className="fas fa-plus" aria-hidden="true"></i>
              Load More — Full Journey
            </button>
          </div>
        )}
      </div>
    </section>
  );
});

// ============================================================================
// News & Events preview — compact cards linking to the full page
// ============================================================================
const NewsPreview = React.memo(function NewsPreview() {
  const navigate = useNavigate();
  const { selectedParivar } = useParivar();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    // Phase 19: the preview follows the visitor's selected Parivar (exactly
    // like the News & Events page) so a switch never leaves another Parivar's
    // updates behind.
    newsEventApi
      .list({ page_size: 100, parivar_id: selectedParivar?.id })
      .then((data) => {
        if (!mounted) return;
        const mapped = (data.results ?? data ?? [])
          .map((b) => ({
            slug: b.slug,
            title: b.title,
            date: b.date || '',
            excerpt: b.excerpt,
            image: mediaUrl(b.image_url),
            tag: b.type === 'event' ? 'Event' : 'News',
            isEvent: b.type === 'event',
          }))
          .slice(0, 3);
        setItems(mapped);
      })
      .catch(() => {
        if (mounted) setItems([]);
      });
    return () => { mounted = false; };
  }, [selectedParivar?.id]);

  return (
    <section className="home-news-section av-section">
      <div className="section-container">
        <Reveal direction="up">
          <SectionHeading
            chip="Updates"
            title="News & Events"
            subtitle="The latest happenings from the ashram and the Datt Parampara."
          />
        </Reveal>

        <div className="home-news-grid">
          {items.map((item, i) => (
            <Reveal key={item.slug} direction="up" delay={i * 120}>
              <Link to={item.isEvent ? `/events/${item.slug}` : `/news/${item.slug}`} className="news-events-card-link">
                <Tilt3D intensity={8}>
                  <div className="news-events-card home-news-card">
                    <div className="card-img-wrap">
                      <img src={item.image} alt={item.title} className="card-image" loading="lazy" />
                      <span className="card-tag">{item.tag}</span>
                    </div>
                    <div className="card-content">
                      <h3 className="card-title">{item.title}</h3>
                      <p className="card-date"><i className="fas fa-calendar-alt"></i> {item.date}</p>
                      <p className="card-excerpt">{item.excerpt}</p>
                      <span className="card-readmore">Read more <i className="fas fa-arrow-right"></i></span>
                    </div>
                  </div>
                </Tilt3D>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal direction="up" delay={200}>
          <div className="home-news-more">
            <CustomButton
              variant="outline-dark"
              className="hero-btn-solid"
              onClick={() => navigate('/news-events')}
            >
              View All News &amp; Events
            </CustomButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
});

function Home() {
  return (
    <>
      <Seo
        title="Shri Avdhoot Vishram | Sarkhej Datt Mandir, Ahmedabad"
        description="Shri Avdhoot Vishram, Sarkhej, Ahmedabad — a sacred spiritual centre dedicated to Lord Dattatreya and Shri Rang Avdhoot Gurumaharaj of Nareshwar. Explore its history, sacred Atmaling and Padukas, Dattopasana, Guru Bhakti, festivals, bhajans and spiritual heritage."
        keywords="Rang Avdhoot, Avdhoot, Nareshwar, Sarkhej Datt mandir, Avdhoot Vishram, Datt mandir, Lord Dattatreya, Shri Rang Avdhoot Gurumaharaj, Dattopasana, Guru Bhakti, bhajans, spiritual centre, Ahmedabad temple, Sarkhej"
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ReligiousOrganization',
          name: 'Shri Avdhoot Vishram',
          alternateName: ['Avdhoot Vishram', 'Sarkhej Datt Mandir', 'Datt Mandir'],
          url: 'https://avdhootvishram.in/',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Sarkhej',
            addressRegion: 'Gujarat',
            addressCountry: 'IN',
          },
        }}
      />
      <HeroSection />
      <YourParivarCard />
      <DivinePresenceSection />
      <React.Suspense fallback={null}>
        <GujaratiCalendar />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <JourneySection />
      </React.Suspense>
      <NewsPreview />
      <React.Suspense fallback={null}>
        <YouTubeLive />
      </React.Suspense>
      <Footer />
    </>
  );
}

export default Home;
